import { toast } from 'react-toastify';
import type { InstallView } from '@dunea/shared';
import { apiInstall, apiListMyRemixes, apiRemixListing, type InstallSource } from '~/lib/api/marketplace';
import { pickResumableRemix, readPendingRemix } from '~/lib/storefront-links';
import { setDraft } from '~/lib/persistence/chat-draft';
import { $integrationRequest } from '~/lib/stores/integrations';
import { applyThemePrompt, installReturnUrl, remixOutcome } from './marketplace-model';

/**
 * Install a listing. Free → the active install. Paid → the browser is sent to
 * Stripe Checkout (resolves 'redirecting'); the panel picks up the
 * `?marketplace=success&install=<id>` marker on the way back.
 */
export async function installListing(
  appId: string,
  input: {
    slug: string;
    permissions: string[];
    settings?: Record<string, unknown>;
    spendCapCredits?: number;
    source?: InstallSource;
  },
): Promise<InstallView | 'redirecting'> {
  const result = await apiInstall(appId, { ...input, returnUrl: installReturnUrl(window.location.href) });

  if (result.status === 'pending_payment') {
    window.location.assign(result.checkoutUrl);
    return 'redirecting';
  }

  return result.install;
}

/** Ask the Dunea AI (through the chat, as if typed) to apply a theme. */
export function requestApplyTheme(name: string, id: string) {
  $integrationRequest.set(applyThemePrompt(name, id));
  toast.info(`Applying ${name}…`);
}

/**
 * Start a new site from a template. Paid → Stripe Checkout (resolves
 * 'redirecting'). Created → the new chat's path, with the template's first
 * prompt left in that chat's composer draft for the owner to send.
 */
export async function remixTemplate(slug: string): Promise<{ path: string } | 'redirecting'> {
  const outcome = remixOutcome(await apiRemixListing(slug, { returnUrl: installReturnUrl(window.location.href) }));

  if (outcome.kind === 'redirect') {
    markPendingRemix(slug);
    window.location.assign(outcome.url);

    return 'redirecting';
  }

  if (outcome.kind === 'error') {
    throw new Error(outcome.message);
  }

  if (outcome.firstPrompt) {
    setDraft(outcome.chatId, outcome.firstPrompt);
  }

  return { path: outcome.path };
}

const PENDING_REMIX_KEY = 'dunea_pending_remix';

function markPendingRemix(slug: string) {
  try {
    localStorage.setItem(PENDING_REMIX_KEY, JSON.stringify({ slug, at: Date.now() }));
  } catch {
    // storage blocked: the resume still finds the remix through me/remixes
  }
}

/** The template whose Checkout we left for (recent only), or null. */
export function pendingRemixSlug(): string | null {
  try {
    return readPendingRemix(localStorage.getItem(PENDING_REMIX_KEY));
  } catch {
    return null;
  }
}

function clearPendingRemix() {
  try {
    localStorage.removeItem(PENDING_REMIX_KEY);
  } catch {
    // ignore
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Back from a template's Checkout: activation is async (Stripe webhook), so
 * poll me/remixes for the new site, seed its composer with the template's
 * first prompt, and open it. Gives up with a toast after ~20s.
 */
export async function resumePaidRemix(
  navigate: (path: string) => void,
  attempts = 8,
  delayMs = 2500,
): Promise<boolean> {
  const slugHint = pendingRemixSlug();

  for (let i = 0; i < attempts; i++) {
    try {
      const remix = pickResumableRemix(await apiListMyRemixes(), Date.now(), slugHint);

      if (remix) {
        // me/remixes carries the template's first prompt, so there's no
        // second remix call (that would create a second copy).
        if (remix.firstPrompt) {
          setDraft(remix.chatId, remix.firstPrompt);
        }

        clearPendingRemix();
        toast.success(`Your copy of ${remix.listingName ?? 'the template'} is ready.`);
        navigate(`/chat/${encodeURIComponent(remix.chatId)}`);

        return true;
      }
    } catch {
      // keep polling
    }

    if (i < attempts - 1) {
      await sleep(delayMs);
    }
  }

  clearPendingRemix();
  toast.info('Payment received. Your new site is still being set up. It will appear in your projects shortly.');

  return false;
}
